/**
 * 🍎 TRANSFORM.MA - Apple-Level Interactions
 * Subtle, refined animations and micro-interactions
 */

(function() {
  'use strict';

  // ===== CONFIGURATION =====
  const CONFIG = {
    scrollThreshold: 20,
    observerThreshold: 0.15,
    headerScrollClass: 'scrolled',
    animationDuration: 300
  };

  // ===== UTILITIES =====
  const throttle = (func, delay) => {
    let lastCall = 0;
    return function(...args) {
      const now = Date.now();
      if (now - lastCall < delay) return;
      lastCall = now;
      return func(...args);
    };
  };

  // ===== HEADER SCROLL EFFECT =====
  function initHeaderScroll() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;

    const handleScroll = throttle(() => {
      if (window.scrollY > CONFIG.scrollThreshold) {
        header.classList.add(CONFIG.headerScrollClass);
      } else {
        header.classList.remove(CONFIG.scrollScrollClass);
      }
    }, 50);

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  // ===== SCROLL TO TOP BUTTON =====
  function initScrollToTop() {
    const button = document.createElement('button');
    button.className = 'scroll-to-top';
    button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <polyline points="15 12 10 7 5 12"></polyline>
      </svg>
    `;
    button.setAttribute('aria-label', 'Retour en haut');
    document.body.appendChild(button);

    const handleScroll = throttle(() => {
      if (window.scrollY > 400) {
        button.classList.add('visible');
      } else {
        button.classList.remove('visible');
      }
    }, 100);

    button.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  // ===== INTERSECTION OBSERVER FOR SUBTLE ANIMATIONS =====
  function initScrollAnimations() {
    const options = {
      threshold: CONFIG.observerThreshold,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }, index * 50);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    // Elements to animate
    const elements = document.querySelectorAll(`
      .card-wrapper,
      .product-grid__item,
      .collection-list__item,
      .footer__block
    `);

    elements.forEach(element => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(16px)';
      element.style.transition = `opacity ${CONFIG.animationDuration}ms cubic-bezier(0.16, 1, 0.3, 1), 
                                  transform ${CONFIG.animationDuration}ms cubic-bezier(0.16, 1, 0.3, 1)`;
      observer.observe(element);
    });
  }

  // ===== SMOOTH SCROLL FOR ANCHORS =====
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#' || href === '#!') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // ===== PRODUCT IMAGE HOVER =====
  function initProductImageHover() {
    const productImages = document.querySelectorAll('.product__media img, .card__media img');
    
    productImages.forEach(img => {
      const parent = img.closest('.product__media, .card__media');
      if (!parent) return;

      parent.addEventListener('mouseenter', function() {
        img.style.transform = 'scale(1.04)';
      });
      
      parent.addEventListener('mouseleave', function() {
        img.style.transform = 'scale(1)';
      });
    });
  }

  // ===== FORM ENHANCEMENT =====
  function initFormEnhancement() {
    const inputs = document.querySelectorAll('input:not([type="submit"]), textarea');
    
    inputs.forEach(input => {
      // Subtle focus effect
      input.addEventListener('focus', function() {
        const parent = this.closest('.field, .field__input');
        if (parent) {
          parent.style.transform = 'translateY(-1px)';
        }
      });
      
      input.addEventListener('blur', function() {
        const parent = this.closest('.field, .field__input');
        if (parent) {
          parent.style.transform = 'translateY(0)';
        }
      });
    });
  }

  // ===== LAZY LOADING ENHANCEMENT =====
  function initLazyLoadEnhancement() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    
    images.forEach(img => {
      img.addEventListener('load', function() {
        this.style.animation = 'fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)';
      });
    });
  }

  // ===== CART ITEM HOVER =====
  function initCartItemHover() {
    const cartItems = document.querySelectorAll('.cart-item');
    
    cartItems.forEach(item => {
      item.addEventListener('mouseenter', function() {
        this.style.transform = 'translateX(4px)';
      });
      
      item.addEventListener('mouseleave', function() {
        this.style.transform = 'translateX(0)';
      });
    });
  }

  // ===== BUTTON RIPPLE EFFECT =====
  function initButtonRipple() {
    const buttons = document.querySelectorAll('.button, button:not(.unstyled)');
    
    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        
        ripple.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          left: ${x}px;
          top: ${y}px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 50%;
          transform: scale(0);
          animation: ripple 0.6s ease-out;
          pointer-events: none;
        `;
        
        this.style.position = 'relative';
        this.style.overflow = 'hidden';
        this.appendChild(ripple);
        
        setTimeout(() => ripple.remove(), 600);
      });
    });
    
    // Add ripple animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes ripple {
        to {
          transform: scale(4);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ===== MENU HOVER ENHANCEMENT =====
  function initMenuHover() {
    const menuItems = document.querySelectorAll('.header__menu-item');
    
    menuItems.forEach(item => {
      item.addEventListener('mouseenter', function() {
        this.style.transform = 'translateY(-1px)';
      });
      
      item.addEventListener('mouseleave', function() {
        this.style.transform = 'translateY(0)';
      });
    });
  }

  // ===== CARD SUBTLE LIFT =====
  function initCardHover() {
    const cards = document.querySelectorAll('.card-wrapper, .card');
    
    cards.forEach(card => {
      card.addEventListener('mouseenter', function() {
        this.style.transform = 'translateY(-4px)';
      });
      
      card.addEventListener('mouseleave', function() {
        this.style.transform = 'translateY(0)';
      });
    });
  }

  // ===== PERFORMANCE OPTIMIZATION =====
  function optimizePerformance() {
    // Passive event listeners are already used
    // Will-change hints for animated elements
    const animatedElements = document.querySelectorAll('.card-wrapper, .product__media');
    animatedElements.forEach(el => {
      el.style.willChange = 'transform';
    });

    // Remove will-change after animation
    setTimeout(() => {
      animatedElements.forEach(el => {
        el.style.willChange = 'auto';
      });
    }, 2000);
  }

  // ===== MAIN INITIALIZATION =====
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    console.log('🍎 Transform.ma Premium Design Initialized');

    // Initialize all features
    initHeaderScroll();
    initScrollToTop();
    initScrollAnimations();
    initSmoothScroll();
    initProductImageHover();
    initFormEnhancement();
    initLazyLoadEnhancement();
    initCartItemHover();
    initButtonRipple();
    initMenuHover();
    initCardHover();
    optimizePerformance();

    // Mark as loaded
    document.body.classList.add('premium-loaded');
  }

  // Start initialization
  init();

  // Export for potential external use
  window.TransformPremium = {
    init,
    version: '2.0.0'
  };

})();
